import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import {
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../containers/ApiContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { PlacementService } from "../../services/PlacementService";
import { TripService } from "../../services/TripService";
import { Hangar, Location, Ramp, TripWithTenant } from "../../types";
import { TripDetailsModal } from "../../screens/Schedule/TripDetailsModal/TripDetailsModal";
import { floorToQuarterHour } from "../../utils/timeUtils";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";

interface Trip {
  tripId: string;
  start: Date;
  end: Date;
  locationId: string;
  locationType: "hangar" | "ramp";
  locationName?: string;
}

interface AircraftInfo {
  tailNumber: string;
  trips: Trip[];
  isCurrentlyHere: boolean;
}

interface Props {
  aircraft: AircraftInfo;
  currentLocation: Location<Hangar | Ramp>;
  currentTime: Date;
  onRefresh: () => void;
  onTimeChange: (time: Date) => void;
}

export const AircraftActionMenu = React.forwardRef<HTMLButtonElement, Props>((
  {
    aircraft,
    currentLocation,
    currentTime,
    onRefresh,
    onTimeChange,
  },
  ref
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const { activeFBO } = useActiveFBOs();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [jumpToMenuAnchor, setJumpToMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [isMoving, setIsMoving] = React.useState(false);
  const [viewTripsOpen, setViewTripsOpen] = React.useState(false);
  const [aircraftTrips, setAircraftTrips] = React.useState<TripWithTenant[]>([]);
  const [selectedTrip, setSelectedTrip] = React.useState<TripWithTenant | null>(null);
  const [tripsLoading, setTripsLoading] = React.useState(false);

  const open = Boolean(anchorEl);
  const jumpToMenuOpen = Boolean(jumpToMenuAnchor);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setJumpToMenuAnchor(null);
  };

  // Get trips at current location
  const tripsAtCurrentLocation = React.useMemo(() => {
    return aircraft.trips.filter(trip =>
      trip.locationId === currentLocation.id
    ).sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [aircraft.trips, currentLocation.id]);

  // Get trips at other locations
  const tripsAtOtherLocations = React.useMemo(() => {
    const locationMap = new Map<string, Trip[]>();

    aircraft.trips
      .filter(trip => {
        // Include unassigned trips (empty locationId) as "other locations"
        // Also include trips at locations other than current
        return !trip.locationId || trip.locationId !== currentLocation.id;
      })
      .forEach(trip => {
        // Use a special key for unassigned trips
        const key = trip.locationId
          ? `${trip.locationType}-${trip.locationId}`
          : 'unassigned';
        if (!locationMap.has(key)) {
          locationMap.set(key, []);
        }
        locationMap.get(key)!.push(trip);
      });

    return Array.from(locationMap.entries()).map(([key, trips]) => ({
      locationKey: key,
      locationId: trips[0].locationId,
      locationType: trips[0].locationType,
      locationName: !trips[0].locationId || trips[0].locationName === "Unassigned"
        ? "Unassigned"
        : (trips[0].locationName || "Unknown Location"),
      trips: trips.sort((a, b) => a.start.getTime() - b.start.getTime()),
    }));
  }, [aircraft.trips, currentLocation.id]);

  // Get current or next upcoming trip
  const currentOrNextTrip = React.useMemo(() => {
    const now = currentTime.getTime();

    // First check for current trip
    const currentTrip = aircraft.trips.find(trip =>
      trip.start.getTime() <= now && trip.end.getTime() > now
    );

    if (currentTrip) return { trip: currentTrip, isCurrent: true };

    // If no current trip, find the next upcoming one
    const futureTrips = aircraft.trips
      .filter(trip => trip.start.getTime() > now)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    if (futureTrips.length > 0) {
      return { trip: futureTrips[0], isCurrent: false };
    }

    return null;
  }, [aircraft.trips, currentTime]);

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(Math.abs(diff) / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    // Format time (24-hour format)
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Add relative time
    if (days === 0 && date.toDateString() === now.toDateString()) {
      return t('widgets.aircraftActionMenu.todayAt', { time: timeStr });
    } else if (days === 1) {
      return t('widgets.aircraftActionMenu.tomorrowAt', { time: timeStr });
    } else if (days === -1) {
      return t('widgets.aircraftActionMenu.yesterdayAt', { time: timeStr });
    } else if (days > 1 && days < 7) {
      return t('widgets.aircraftActionMenu.inDaysAt', { days, time: timeStr });
    } else {
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
  };

  const handleJumpToTime = (trip: Trip) => {
    // Pass the exact trip start time - the URL hook will handle proper snapping
    onTimeChange(trip.start);
    handleClose();
  };

  const handleNavigateToLocation = (locationId: string, locationType: "hangar" | "ramp", time: Date) => {
    const path = locationType === "hangar"
      ? `/hangars/${locationId}/schedule`
      : `/ramps/${locationId}/schedule`;

    // Pass the exact time - the destination route will handle proper snapping
    const timeParam = time.toISOString();
    navigate(`${path}?t=${timeParam}`);
    handleClose();
  };

  const handleAssignEntireTrip = async () => {
    if (!currentOrNextTrip || !postgrest || !activeFBO?.id) {
      snackbar.notify({
        text: t('widgets.aircraftActionMenu.noActiveTripWarning'),
        severity: "warning",
      });
      return;
    }

    setIsMoving(true);
    try {
      const { trip } = currentOrNextTrip;

      // First, get the full trip data to get actual arrival/departure dates
      const tripService = new TripService(postgrest);
      const trips = await tripService.getTripsByTailNumber(activeFBO.id, aircraft.tailNumber);
      const fullTrip = trips.find(t => t.trip_id === trip.tripId);

      if (!fullTrip) {
        throw new Error(t('widgets.aircraftActionMenu.tripDetailsNotFound'));
      }

      // Use rebuildTripCoverage to assign the entire trip to this location
      const placementService = new PlacementService(postgrest);
      await placementService.rebuildTripCoverage(trip.tripId, [{
        start_time: fullTrip.arrival_date,
        end_time: fullTrip.departure_date,
        hangar_id: currentLocation.type === 'hangar' ? currentLocation.id : null,
        ramp_id: currentLocation.type === 'ramp' ? currentLocation.id : null,
        x: null,
        y: null,
        angle: null
      }]);

      snackbar.notify({
        text: t('widgets.aircraftActionMenu.assignedForEntireTrip', {
          tailNumber: aircraft.tailNumber,
          locationName: currentLocation.name
        }),
        severity: "success",
      });

      // Trigger refresh with delay to ensure DB has updated
      setTimeout(() => {
        onRefresh();
      }, 500);
      handleClose();
    } catch (error: any) {
      console.error("Failed to assign entire trip:", error);
      snackbar.notify({
        text: error.message || t('widgets.aircraftActionMenu.failedAssignEntireTrip'),
        severity: "error",
      });
    } finally {
      setIsMoving(false);
    }
  };

  const handleAssignHere = async () => {
    if (!currentOrNextTrip || !postgrest) {
      snackbar.notify({
        text: t('widgets.aircraftActionMenu.noActiveTripWarning'),
        severity: "warning",
      });
      return;
    }

    setIsMoving(true);
    try {
      const placementService = new PlacementService(postgrest);
      const { trip, isCurrent } = currentOrNextTrip;

      // If it's a future trip, assign from its start time
      // If it's current, assign from current time
      // Always floor to quarter hour for proper alignment
      const assignFromTime = isCurrent
        ? floorToQuarterHour(currentTime)
        : floorToQuarterHour(trip.start);

      await placementService.assignToLocationWithGapFilling(
        trip.tripId,
        currentLocation.id,
        currentLocation.type,
        assignFromTime.toISOString(),
        undefined, // to trip end
        true, // exclude conflicts
        { x: null, y: null, angle: null } // clear position
      );

      snackbar.notify({
        text: t('widgets.aircraftActionMenu.assigned', {
          tailNumber: aircraft.tailNumber,
          locationName: currentLocation.name
        }),
        severity: "success",
      });

      // Trigger refresh with delay to ensure DB has updated
      setTimeout(() => {
        onRefresh();
      }, 500);
      handleClose();
    } catch (error: any) {
      console.error("Failed to move aircraft:", error);
      snackbar.notify({
        text: error.message || t('widgets.aircraftActionMenu.failedAssignAircraft'),
        severity: "error",
      });
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <>
      <IconButton
        ref={ref}
        size="small"
        onClick={handleClick}
        disabled={isMoving}
        sx={{
          "&:hover": {
            backgroundColor: "action.hover",
            "& .MuiSvgIcon-root": {
              transform: "rotate(90deg)",
              transition: "transform 0.2s",
            },
          },
        }}
      >
        {isMoving ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {/* Jump to trips at any location */}
        {(tripsAtCurrentLocation.length > 0 || tripsAtOtherLocations.length > 0) && (
          <>
            <MenuItem
              onClick={(e) => setJumpToMenuAnchor(e.currentTarget)}
            >
              <ListItemIcon>
                <AccessTimeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('widgets.aircraftActionMenu.jumpTo')}</ListItemText>
              <ChevronRightIcon fontSize="small" />
            </MenuItem>
            {/* Only show divider if assign options follow */}
            {!aircraft.isCurrentlyHere && currentOrNextTrip && <Divider />}
          </>
        )}

        {/* Assign here options - only show if not already here and has a trip */}
        {!aircraft.isCurrentlyHere && currentOrNextTrip && (
          <>
            <MenuItem onClick={handleAssignHere} disabled={isMoving}>
              <ListItemIcon>
                <MoveUpIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('widgets.aircraftActionMenu.assignHere')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAssignEntireTrip} disabled={isMoving}>
              <ListItemIcon>
                <MoveUpIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('widgets.aircraftActionMenu.assignHereEntireTrip')}</ListItemText>
            </MenuItem>
          </>
        )}

        {/* Only show divider if there were items above */}
        {(tripsAtCurrentLocation.length > 0 || tripsAtOtherLocations.length > 0 || (!aircraft.isCurrentlyHere && currentOrNextTrip)) && <Divider />}

        {/* View trips */}
        <MenuItem
          onClick={async () => {
            if (!activeFBO?.id || !aircraft.tailNumber) return;

            setTripsLoading(true);
            try {
              const tripService = new TripService(postgrest);
              const trips = await tripService.getTripsByTailNumber(
                activeFBO.id,
                aircraft.tailNumber
              );

              setAircraftTrips(trips);
              // Find the current trip if any
              const now = currentTime.getTime();
              const currentTripData = trips.find(t => {
                const start = new Date(t.arrival_date).getTime();
                const end = new Date(t.departure_date).getTime();
                return start <= now && end >= now;
              });

              if (currentTripData) {
                setSelectedTrip(currentTripData);
              } else if (trips.length > 0) {
                setSelectedTrip(trips[0]);
              }

              handleClose();
              setViewTripsOpen(true);
            } catch (error) {
              console.error('Failed to fetch trips:', error);
              snackbar.notify({
                text: t('widgets.aircraftActionMenu.failedLoadTrips'),
                severity: 'error',
              });
            } finally {
              setTripsLoading(false);
            }
          }}
          disabled={tripsLoading}
        >
          <ListItemIcon>
            <FlightTakeoffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {tripsLoading ? <CircularProgress size={14} /> : t('widgets.aircraftActionMenu.viewTrips')}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Combined Jump to submenu */}
      <Menu
        anchorEl={jumpToMenuAnchor}
        open={jumpToMenuOpen}
        onClose={() => setJumpToMenuAnchor(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {/* Trips at current location */}
        {tripsAtCurrentLocation.length > 0 && (
          <>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {t('widgets.aircraftActionMenu.hereAt', { locationName: currentLocation.name })}
              </Typography>
            </MenuItem>
            {tripsAtCurrentLocation.map((trip, index) => (
              <MenuItem
                key={`current-${trip.tripId}-${index}`}
                onClick={() => handleJumpToTime(trip)}
                sx={{ pl: 4 }}
              >
                <ListItemText>
                  <Typography variant="body2">{formatTime(trip.start)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('widgets.aircraftActionMenu.hoursStay', {
                      hours: Math.round((trip.end.getTime() - trip.start.getTime()) / (1000 * 60 * 60))
                    })}
                  </Typography>
                </ListItemText>
              </MenuItem>
            ))}
          </>
        )}

        {/* Show divider if both sections have content */}
        {tripsAtCurrentLocation.length > 0 && tripsAtOtherLocations.length > 0 && <Divider />}

        {/* Trips at other locations */}
        {tripsAtOtherLocations.length > 0 && (
          <>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {t('widgets.aircraftActionMenu.otherLocations')}
              </Typography>
            </MenuItem>
            {tripsAtOtherLocations.map((location) => (
              <React.Fragment key={location.locationKey}>
                {location.trips.slice(0, 3).map((trip, index) => (
                  <MenuItem
                    key={`other-${trip.tripId}-${index}`}
                    onClick={() => {
                      // For unassigned trips, just jump to the time
                      if (!trip.locationId) {
                        handleJumpToTime(trip);
                      } else {
                        handleNavigateToLocation(
                          trip.locationId,
                          trip.locationType,
                          trip.start
                        );
                      }
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText>
                      <Typography variant="body2">
                        {location.locationName}: {formatTime(trip.start)}
                      </Typography>
                    </ListItemText>
                  </MenuItem>
                ))}
                {location.trips.length > 3 && (
                  <MenuItem disabled sx={{ pl: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('widgets.aircraftActionMenu.andMoreAt', {
                        count: location.trips.length - 3,
                        locationName: location.locationName
                      })}
                    </Typography>
                  </MenuItem>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </Menu>

      {/* Trip Details Modal */}
      <TripDetailsModal
        open={viewTripsOpen}
        trip={selectedTrip}
        trips={aircraftTrips}
        onClose={() => {
          setViewTripsOpen(false);
          setSelectedTrip(null);
        }}
        onNavigate={(newTripId) => {
          const newTrip = aircraftTrips.find(t => t.trip_id === newTripId);
          if (newTrip) {
            setSelectedTrip(newTrip);
          }
        }}
        onTripChanged={async () => {
          // Refresh trips after changes
          if (!activeFBO?.id || !aircraft.tailNumber) return;

          try {
            const tripService = new TripService(postgrest);
            const trips = await tripService.getTripsByTailNumber(
              activeFBO.id,
              aircraft.tailNumber
            );
            setAircraftTrips(trips);
          } catch (error) {
            console.error('Failed to refresh trips:', error);
          }

          // Also refresh the main view
          onRefresh();
        }}
        currentTimestamp={currentTime}
      />
    </>
  );
});

AircraftActionMenu.displayName = 'AircraftActionMenu';